import type {
  StudyMissionSelectDto,
  StudySelectDto,
  StudySwimlaneSelectDto,
  StudyTaskReadinessSelectDto,
  StudyThreadSelectDto,
} from '@@types/api.types';
import type { EmptyStateContainerProps } from '@components/shared/empty-state-container.component';
import { Tile } from '@components/shared/tile.component';
import { useGetMeiaStudyThreadSwimlanes, useGetMeiaStudyThreadTaskReadiness } from '@queries/meia.query';
import { type FC, useMemo } from 'react';
import type { MeiaPhaseKey } from '../meia.constants';
import { getMeiaMissionCode, getMeiaMissionDesiredOutcome, getMeiaMissionTitle } from './meia-mission-display.util';
import { MeiaMissionScopeCard } from './meia-mission-scope-card.component';
import {
  getMeiaMissionReadinessStatus,
  getMeiaMissionReadinessTooltip,
  getMeiaThreadReadinessStatus,
} from './meia-readiness.util';
import type { MeiaTaskReadinessStatus } from './meia-task-column.util';
import { MeiaTaskReadinessStatusIcon } from './meia-task-readiness-status-icon.component';

type MeiaMissionScopeStripProps = {
  /** When provided and there are no missions, the tile renders this empty state instead of the mission cards. */
  emptyState?: EmptyStateContainerProps;
  /** Optional header action button (e.g. add mission / jump back to P1). */
  headerButton?: { onClick: () => void; text: string };
  missions: StudyMissionSelectDto[];
  onSelectMission: (missionId: string) => void;
  /** Current phase — drives the mission readiness waterfall (framing only on P1, framing + threads after). */
  phase: MeiaPhaseKey;
  selectedMissionId?: string;
  study: StudySelectDto;
  threads: StudyThreadSelectDto[];
  /** Tile title label — defaults to "Mission Scope". */
  titleLabel?: string;
};

export const MeiaMissionScopeStrip: FC<MeiaMissionScopeStripProps> = (props) => {
  const {
    emptyState,
    headerButton,
    missions,
    onSelectMission,
    phase,
    selectedMissionId,
    study,
    threads,
    titleLabel = 'Mission Scope',
  } = props;
  const kopProblem = study.keyOperationalProblem?.trim();
  const { swimlanes } = useGetMeiaStudyThreadSwimlanes({ studyId: study.id, threads });
  const { taskReadiness } = useGetMeiaStudyThreadTaskReadiness({ studyId: study.id, threads });

  // Per-thread readiness, rolled up so each mission card can waterfall its threads into its own status.
  const threadStatusById = useMemo(() => {
    const swimlanesByThread = new Map<string, StudySwimlaneSelectDto[]>();
    for (const swimlane of swimlanes) {
      const list = swimlanesByThread.get(swimlane.threadId) ?? [];
      list.push(swimlane);
      swimlanesByThread.set(swimlane.threadId, list);
    }
    const taskReadinessByThread = new Map<string, StudyTaskReadinessSelectDto[]>();
    for (const entry of taskReadiness) {
      const list = taskReadinessByThread.get(entry.threadId) ?? [];
      list.push(entry);
      taskReadinessByThread.set(entry.threadId, list);
    }

    const result = new Map<string, MeiaTaskReadinessStatus>();
    for (const thread of threads) {
      result.set(
        thread.id,
        getMeiaThreadReadinessStatus({
          swimlanes: swimlanesByThread.get(thread.id) ?? [],
          taskReadiness: taskReadinessByThread.get(thread.id) ?? [],
        }),
      );
    }
    return result;
  }, [swimlanes, taskReadiness, threads]);

  return (
    <Tile
      contentClassName="gap-3"
      emptyState={emptyState}
      header={{
        button: headerButton,
        title: (
          <span className="inline-flex items-center gap-2">
            <span>{titleLabel}</span>
            <span className="text-xs font-normal text-gray-400">
              ({missions.length} {missions.length === 1 ? 'mission' : 'missions'} on this KOP)
            </span>
          </span>
        ),
      }}
      isEmpty={Boolean(emptyState) && missions.length === 0}
    >
      {kopProblem && <p className="line-clamp-2 text-xs text-gray-500">{kopProblem}</p>}
      <div className="flex flex-wrap gap-2">
        {missions.map((mission, index) => {
          const missionThreads = threads.filter((thread) => thread.missionId === mission.id);
          const readinessStatus = getMeiaMissionReadinessStatus({
            mission,
            phase,
            threadStatuses: missionThreads.map((thread) => threadStatusById.get(thread.id) ?? 'empty'),
          });

          return (
            <MeiaMissionScopeCard
              code={getMeiaMissionCode({ index, mission })}
              desiredOutcome={getMeiaMissionDesiredOutcome(mission)}
              isSelected={mission.id === selectedMissionId}
              key={mission.id}
              onSelect={() => onSelectMission(mission.id)}
              statusIndicator={
                <MeiaTaskReadinessStatusIcon
                  className="mt-px"
                  label="Mission readiness"
                  status={readinessStatus}
                  tooltip={getMeiaMissionReadinessTooltip(readinessStatus, phase)}
                />
              }
              threadCount={missionThreads.length}
              title={getMeiaMissionTitle(mission)}
            />
          );
        })}
      </div>
    </Tile>
  );
};
